// @ts-check

import React from 'react';
import { Box, Button, Flex, Text } from '~/design-system';

const variants = {
  success: {
    backgroundColor: '#e0ffce',
    color: '#4c852a',
    borderColor: '#7ee244',
  },

  warning: {
    backgroundColor: '#fdf7b6',
    color: '#ac8615',
    borderColor: '#ac8615',
  },

  error: {
    backgroundColor: '#fac9c3',
    color: '#c1250d',
    borderColor: '#c1250d',
  },
};

/**
 * @typedef {object} Notification
 * @property {string} message The message to display.
 * @property {boolean} [success=false] If the message is a success or error message
 */

/**
 * A notification component.
 * @param {object} props
 * @param {string} props.message The message to display.
 * @param {()=>void} props.onClose The callback to close the window.
 * @param {string} [props.variant] The variant (sucess, warning or error).
 * @param {number} [props.timeout]
 *    The timeout to make the notification disappear.
 *    Defaults to infinite for errors,
 *    and to 3s for success/warning messages.
 * @returns {React.ReactElement}
 */
export default function Notification({
  message,
  onClose,
  variant = 'success',
  timeout = variant === 'error' ? undefined : 3000,
}) {
  React.useEffect(() => {
    if (timeout) {
      const notificationTimer = setTimeout(onClose, timeout);
      return () => clearTimeout(notificationTimer);
    }
  });

  return (
    <Flex
      sx={{
        padding: 1,
        position: 'fixed',
        minHeight: 'initial',
        minWidth: '320px',
        right: 3,
        top: 'navbar',
        marginTop: 3,
        border: 1,
        zIndex: 'modal',
        // Make links the same color as the variant.
        '& a': { color: 'inherit' },

        ...variants[variant],
      }}
    >
      <Box>
        <Text>{message}</Text>
      </Box>
      <Button variant="text" sx={{ marginLeft: 'auto' }} onClick={onClose}>
        ✕
      </Button>
    </Flex>
  );
}
